import { useState } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { useTranslation, Trans } from 'react-i18next'
import { useAuthContext } from '@/contexts/auth-context'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Header } from '@/components/ui/header'
import { Mail, Lock, Loader2, ChevronRight, Check, X } from 'lucide-react'
import { toast } from 'sonner'

interface PasswordRequirement {
  labelKey: string
  test: (password: string) => boolean
}

const passwordRequirements: PasswordRequirement[] = [
  { labelKey: 'auth.pwReqMinLength', test: (p) => p.length >= 8 },
  { labelKey: 'auth.pwReqNumber', test: (p) => /\d/.test(p) },
  { labelKey: 'auth.pwReqUppercase', test: (p) => /[A-Z]/.test(p) },
  { labelKey: 'auth.pwReqLowercase', test: (p) => /[a-z]/.test(p) },
]

export function SignupPage() {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const { signUp } = useAuthContext()

  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [showPasswordRequirements, setShowPasswordRequirements] = useState(false)

  const allRequirementsMet = passwordRequirements.every((req) => req.test(password))
  const passwordsMatch = password === confirmPassword && confirmPassword.length > 0

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!allRequirementsMet) {
      toast.error(t('auth.toastMeetPasswordRequirements'))
      return
    }

    if (!passwordsMatch) {
      toast.error(t('auth.toastPasswordsDoNotMatch'))
      return
    }

    setIsLoading(true)
    const { error } = await signUp(email, password)
    setIsLoading(false)

    if (error) {
      toast.error(error.message || t('auth.toastCreateAccountFailed'))
      return
    }

    toast.success(t('auth.toastAccountCreated'))
    navigate('/confirm-email', { state: { email } })
  }

  return (
    <div className="min-h-screen bg-warm-gray flex flex-col">
      <Header />

      <div className="flex-1 flex">
        {/* Left Panel - Branding (desktop only) */}
        <div className="hidden lg:flex lg:w-1/2 bg-primary p-12 flex-col justify-between items-start">
          <div />
          <div className="space-y-6">
            <h1 className="text-4xl xl:text-5xl font-bold text-white leading-tight text-balance">
              {t('auth.signupHeroTitle')}
            </h1>
            <p className="text-lg text-white/80 max-w-md">{t('auth.signupHeroSubtitle')}</p>
          </div>
          <div className="flex items-center gap-4">
            <div className="flex -space-x-3">
              {[1, 2, 3, 4].map((i) => (
                <div
                  key={i}
                  className="w-10 h-10 rounded-full bg-white/20 border-2 border-white/30 flex items-center justify-center text-white text-sm font-medium"
                >
                  {String.fromCharCode(64 + i)}
                </div>
              ))}
            </div>
            <p className="text-white/80 text-sm">
              <Trans
                i18nKey="auth.businessesOnboard"
                components={{ strong: <span className="font-semibold text-white" /> }}
              />
            </p>
          </div>
        </div>

        {/* Right Panel - Signup Form */}
        <div className="flex-1 flex flex-col justify-center p-8 lg:p-12">
          <div className="max-w-md mx-auto w-full bg-white rounded-2xl border border-gray-200 p-8 shadow-sm space-y-8">
            <div className="space-y-2">
              <h2 className="text-3xl font-bold text-gray-900">{t('auth.createAccount')}</h2>
              <p className="text-gray-500">{t('auth.createAccountSubtitle')}</p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email">{t('auth.emailLabel')}</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    id="email"
                    type="email"
                    placeholder={t('auth.emailPlaceholder')}
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="pl-10"
                    required
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="password">{t('auth.passwordLabel')}</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    id="password"
                    type="password"
                    placeholder={t('auth.passwordCreatePlaceholder')}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    onFocus={() => setShowPasswordRequirements(true)}
                    className="pl-10"
                    required
                  />
                </div>
                {showPasswordRequirements && (
                  <div className="p-3 rounded-lg bg-gray-50 border border-gray-200 space-y-2">
                    {passwordRequirements.map((req) => {
                      const met = req.test(password)
                      return (
                        <div
                          key={req.labelKey}
                          className={`flex items-center gap-2 text-sm ${
                            met ? 'text-green-600' : 'text-gray-500'
                          }`}
                        >
                          {met ? <Check className="w-4 h-4" /> : <X className="w-4 h-4" />}
                          {t(req.labelKey)}
                        </div>
                      )
                    })}
                  </div>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirmPassword">{t('auth.confirmPasswordLabel')}</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    id="confirmPassword"
                    type="password"
                    placeholder={t('auth.confirmPasswordPlaceholder')}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="pl-10"
                    required
                  />
                </div>
                {confirmPassword && !passwordsMatch && (
                  <p className="text-sm text-red-500">{t('auth.passwordsDoNotMatch')}</p>
                )}
              </div>

              <Button
                type="submit"
                size="lg"
                className="w-full h-12 text-base group"
                disabled={isLoading || !allRequirementsMet || !passwordsMatch}
              >
                {isLoading ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <>
                    {t('auth.createAccount')}
                    <ChevronRight className="w-4 h-4 ml-2 transition-transform group-hover:translate-x-1" />
                  </>
                )}
              </Button>
            </form>

            <div className="text-center space-y-4">
              <p className="text-sm text-gray-500">
                {t('auth.alreadyHaveAccount')}{' '}
                <Link to="/login" className="font-medium text-accent hover:underline">
                  {t('auth.signIn')}
                </Link>
              </p>
              <p className="text-xs text-gray-400">
                <Trans
                  i18nKey="auth.signupLegal"
                  components={{
                    terms: <a href="#" className="underline hover:text-gray-700" />,
                    privacy: <a href="#" className="underline hover:text-gray-700" />,
                  }}
                />
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
